import { useEffect, useRef, useCallback, useState } from 'react';
import { websocketService, WebSocketEvent, WebSocketStatus } from '../services/websocketService';
import { useAuth } from './useAuth';
import { useNotifications } from '../context/NotificationContext';

// Hook options interface
export interface UseWebSocketOptions {
  autoConnect?: boolean;
  reconnectOnAuth?: boolean;
  showConnectionNotifications?: boolean;
  subscribeToNotifications?: boolean;
}

// WebSocket hook return type
export interface UseWebSocketReturn {
  isConnected: boolean;
  status: WebSocketStatus;
  connect: () => Promise<void>;
  disconnect: () => void;
  subscribe: (event: WebSocketEvent, callback: (data: any) => void) => () => void;
  send: (event: WebSocketEvent, data: any) => void;
  subscriptionCount: number;
  activeEvents: WebSocketEvent[];
}

// Main WebSocket hook
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    autoConnect = true,
    reconnectOnAuth = true,
    showConnectionNotifications = true,
    subscribeToNotifications = true,
  } = options;

  const { isAuthenticated } = useAuth();
  // ✅ Move useNotifications hook to top level
  const { showSuccess, showWarning, showError, showInfo } = useNotifications();
  
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [activeEvents, setActiveEvents] = useState<WebSocketEvent[]>([]);
  
  const statusUnsubscribeRef = useRef<(() => void) | null>(null);
  const subscriptionsRef = useRef<Set<() => void>>(new Set());

  // Update local state when WebSocket status changes
  useEffect(() => {
    const unsubscribe = websocketService.onStatusChange((newStatus) => {
      setStatus(newStatus);
      
      if (showConnectionNotifications) {
        switch (newStatus) {
          case 'connected':
            showSuccess('Connected', 'Real-time updates are now active');
            break;
          case 'disconnected':
            showWarning('Disconnected', 'Real-time updates are paused');
            break;
          case 'error':
            showError('Connection Error', 'Failed to connect to real-time service');
            break;
          case 'reconnecting':
            showWarning('Reconnecting', 'Attempting to restore real-time connection');
            break;
        }
      }
    });

    statusUnsubscribeRef.current = unsubscribe;
    setStatus(websocketService.getStatus());

    return () => {
      if (statusUnsubscribeRef.current) {
        statusUnsubscribeRef.current();
      }
    };
  }, [showConnectionNotifications, showSuccess, showWarning, showError]);

  // Update subscription statistics
  const updateStats = useCallback(() => {
    setSubscriptionCount(websocketService.getSubscriptionCount());
    setActiveEvents(websocketService.getActiveEvents());
  }, []);

  // Connect function
  const connect = useCallback(async () => {
    console.log('🔌 useWebSocket: connect() called, isAuthenticated:', isAuthenticated);
    if (!isAuthenticated) {
      console.warn('🔌 useWebSocket: Cannot connect - user not authenticated');
      return;
    }

    // Get token from both storages
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (!token) {
      console.warn('🔌 useWebSocket: Cannot connect - no access token found');
      return;
    }

    try {
      await websocketService.connect(token);  // ✅ Pass token!
      updateStats();
    } catch (error) {
      console.error('🔌 useWebSocket: Connection failed:', error);
    }
  }, [isAuthenticated, updateStats]);

  // Disconnect function
  const disconnect = useCallback(() => {
    websocketService.disconnect();
    updateStats();
  }, [updateStats]);

  // Subscribe function with cleanup tracking
  const subscribe = useCallback((event: WebSocketEvent, callback: (data: any) => void) => {
    const unsubscribe = websocketService.subscribe(event, callback);
    
    // Track this subscription for cleanup
    subscriptionsRef.current.add(unsubscribe);
    updateStats();

    // Return enhanced unsubscribe function
    return () => {
      unsubscribe();
      subscriptionsRef.current.delete(unsubscribe);
      updateStats();
    };
  }, [updateStats]);

  // Send function
  const send = useCallback((event: WebSocketEvent, data: any) => {
    websocketService.emit(event, data);  // ✅ Use 'emit'!
  }, []);

  // Auto-connect when authenticated
  useEffect(() => {
    // Attempt connection if disconnected or in error state
    const shouldConnect = autoConnect && isAuthenticated && (status === 'disconnected' || status === 'error');
    
    if (shouldConnect) {
      console.log(`🔌 useWebSocket: Auto-connecting (status: ${status})`);
      connect();
    }
  }, [autoConnect, isAuthenticated, status, connect]);

  // Reconnect when authentication changes
  useEffect(() => {
    if (reconnectOnAuth) {
      if (isAuthenticated && (status === 'disconnected' || status === 'error')) {
        connect();
      } else if (!isAuthenticated && status === 'connected') {
        disconnect();
      }
    }
  }, [reconnectOnAuth, isAuthenticated, status, connect, disconnect]);

  // ✅ Fixed: Subscribe to notification events if enabled
  useEffect(() => {
    if (!subscribeToNotifications || !isAuthenticated) return;

    const unsubscribe = websocketService.subscribeToNotifications((data) => {
      // ✅ Now using notification functions from hook result at top level
      switch (data.type) {
        case 'success':
          showSuccess(data.title || 'Success', data.message);
          break;
        case 'error':
          showError(data.title || 'Error', data.message);
          break;
        case 'warning':
          showWarning(data.title || 'Warning', data.message);
          break;
        case 'info':
        default:
          showInfo(data.title || 'Info', data.message);
          break;
      }
    });

    subscriptionsRef.current.add(unsubscribe);

    return () => {
      unsubscribe();
      subscriptionsRef.current.delete(unsubscribe);
    };
  }, [subscribeToNotifications, isAuthenticated, showSuccess, showError, showWarning, showInfo]); // ✅ Added all notification functions to dependencies

  // Cleanup all subscriptions on unmount
  useEffect(() => {
    return () => {
      subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
      subscriptionsRef.current.clear();
    };
  }, []);

  return {
    isConnected: status === 'connected',
    status,
    connect,
    disconnect,
    subscribe,
    send,
    subscriptionCount,
    activeEvents,
  };
}

// Specialized hook for dashboard real-time updates
export function useDashboardWebSocket() {
  const webSocket = useWebSocket();
  const [dashboardData, setDashboardData] = useState<any>(null);

  useEffect(() => {
    if (!webSocket.isConnected) return;

    const unsubscribe = webSocket.subscribe('dashboard:update', (data) => {
      setDashboardData(data);
    });

    return unsubscribe;
  }, [webSocket.isConnected, webSocket.subscribe]); // ✅ Added missing dependencies

  return {
    ...webSocket,
    dashboardData,
  };
}

// Specialized hook for booking real-time updates
export function useBookingWebSocket() {
  const webSocket = useWebSocket();
  const [bookingUpdates, setBookingUpdates] = useState<any[]>([]);

  useEffect(() => {
    if (!webSocket.isConnected) return;

    const unsubscribe = webSocket.subscribe('booking:updated', (data) => {
      setBookingUpdates(prev => [data, ...prev].slice(0, 50)); // Keep last 50 updates
    });

    return unsubscribe;
  }, [webSocket.isConnected, webSocket.subscribe]); // ✅ Added missing dependencies

  const clearUpdates = useCallback(() => {
    setBookingUpdates([]);
  }, []);

  return {
    ...webSocket,
    bookingUpdates,
    clearUpdates,
  };
}

// Specialized hook for driver location tracking
export function useDriverLocationWebSocket() {
  const webSocket = useWebSocket();
  const [driverLocations, setDriverLocations] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!webSocket.isConnected) return;

    const unsubscribe = webSocket.subscribe('driver:location_updated', (data) => {
      setDriverLocations(prev => ({
        ...prev,
        [data.driverId]: {
          lat: data.lat,
          lng: data.lng,
          heading: data.heading,
          speed: data.speed,
          timestamp: data.timestamp,
        },
      }));
    });

    return unsubscribe;
  }, [webSocket.isConnected, webSocket.subscribe]); // ✅ Added missing dependencies

  const getDriverLocation = useCallback((driverId: string) => {
    return driverLocations[driverId] || null;
  }, [driverLocations]);

  const clearDriverLocation = useCallback((driverId: string) => {
    setDriverLocations(prev => {
      const updated = { ...prev };
      delete updated[driverId];
      return updated;
    });
  }, []);

  return {
    ...webSocket,
    driverLocations,
    getDriverLocation,
    clearDriverLocation,
  };
}

// Hook for managing multiple WebSocket subscriptions
export function useWebSocketSubscriptions() {
  const webSocket = useWebSocket();
  const subscriptionsRef = useRef<Map<string, () => void>>(new Map());

  const addSubscription = useCallback((
    key: string,
    event: WebSocketEvent,
    callback: (data: any) => void
  ) => {
    // Remove existing subscription with the same key
    const existingUnsubscribe = subscriptionsRef.current.get(key);
    if (existingUnsubscribe) {
      existingUnsubscribe();
    }

    // Add new subscription
    const unsubscribe = webSocket.subscribe(event, callback);
    subscriptionsRef.current.set(key, unsubscribe);

    return () => {
      unsubscribe();
      subscriptionsRef.current.delete(key);
    };
  }, [webSocket.subscribe]); // ✅ Added missing dependency

  const removeSubscription = useCallback((key: string) => {
    const unsubscribe = subscriptionsRef.current.get(key);
    if (unsubscribe) {
      unsubscribe();
      subscriptionsRef.current.delete(key);
    }
  }, []);

  const removeAllSubscriptions = useCallback(() => {
    subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
    subscriptionsRef.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      removeAllSubscriptions();
    };
  }, [removeAllSubscriptions]);

  return {
    ...webSocket,
    addSubscription,
    removeSubscription,
    removeAllSubscriptions,
    activeSubscriptions: Array.from(subscriptionsRef.current.keys()),
  };
}

// Hook for WebSocket connection monitoring and diagnostics
export function useWebSocketDiagnostics() {
  const webSocket = useWebSocket({ showConnectionNotifications: false });
  const [connectionHistory, setConnectionHistory] = useState<Array<{
    status: WebSocketStatus;
    timestamp: Date;
  }>>([]);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Track connection status changes
  useEffect(() => {
    const unsubscribe = websocketService.onStatusChange((status) => {
      setConnectionHistory(prev => [
        { status, timestamp: new Date() },
        ...prev.slice(0, 99) // Keep last 100 status changes
      ]);

      if (status === 'reconnecting') {
        setReconnectAttempts(prev => prev + 1);
      } else if (status === 'connected') {
        setReconnectAttempts(0);
      }
    });

    return unsubscribe;
  }, []); // ✅ Empty dependency array is correct here as we're subscribing to external service

  const getConnectionStats = useCallback(() => {
    const now = new Date();
    const lastHour = new Date(now.getTime() - 60 * 60 * 1000);
    
    const recentHistory = connectionHistory.filter(
      entry => entry.timestamp > lastHour
    );

    const disconnections = recentHistory.filter(
      entry => entry.status === 'disconnected'
    ).length;

    const totalUptime = recentHistory.reduce((uptime, entry, index, array) => {
      if (entry.status === 'connected' && index < array.length - 1) {
        const nextEntry = array[index + 1];
        const duration = nextEntry.timestamp.getTime() - entry.timestamp.getTime();
        return uptime + duration;
      }
      return uptime;
    }, 0);

    const uptimePercentage = recentHistory.length > 0 
      ? (totalUptime / (60 * 60 * 1000)) * 100 
      : 0;

    return {
      isHealthy: webSocket.isConnected && disconnections < 3,
      uptimePercentage: Math.min(100, Math.max(0, uptimePercentage)),
      disconnectionsLastHour: disconnections,
      reconnectAttempts,
      lastConnectionTime: connectionHistory.find(e => e.status === 'connected')?.timestamp,
      lastDisconnectionTime: connectionHistory.find(e => e.status === 'disconnected')?.timestamp,
    };
  }, [connectionHistory, reconnectAttempts, webSocket.isConnected]);

  return {
    ...webSocket,
    connectionHistory: connectionHistory.slice(0, 20), // Return last 20 entries
    reconnectAttempts,
    connectionStats: getConnectionStats(),
  };
}

export default useWebSocket;